import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Copy, Gem, Loader, Target, Users } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useWheel500Store from '../store/useWheel500Store'
import Wheel500Wheel from '../components/Wheel500Wheel'
import Wheel500BetsFeed from '../components/Wheel500BetsFeed'
import { WHEEL500_SECTORS, WHEEL500_ZONE_CONFIG, WHEEL500_ZONE_KEYS } from '../config/wheel500Config'
import { hapticNotify } from '../../../utils/haptic'

const SHORT_ROUND_ID_LENGTH = 10

const Wheel500Round = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation()
  const { loadRoundById } = useWheel500Store()

  const [round, setRound] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadRound = async () => {
      setIsLoading(true)

      try {
        const data = await loadRoundById(id)
        setRound(data)
      } catch (error) {
        hapticNotify('error')
        toast.error(t('wheel500.round.loadError', 'Не удалось загрузить раунд'))
      } finally {
        setIsLoading(false)
      }
    }

    loadRound()
  }, [id, loadRoundById, t])

  const bets = useMemo(() => {
    if (Array.isArray(round?.bets)) return round.bets
    if (Array.isArray(round?.users)) return round.users
    return []
  }, [round])

  const sums = {
    x2: Number(round?.sum_x2 || 0),
    x3: Number(round?.sum_x3 || 0),
    x5: Number(round?.sum_x5 || 0),
    x50: Number(round?.sum_x50 || 0)
  }

  const totalBank = sums.x2 + sums.x3 + sums.x5 + sums.x50

  const participantsCount = useMemo(() => {
    return new Set(
      bets.map((bet, index) => bet?.chat_id || `${bet?.name || 'player'}-${index}`)
    ).size
  }, [bets])

  const myBets = useMemo(() => bets.filter((bet) => bet?.your_bet || bet?.you), [bets])
  const myTotalSum = myBets.reduce((acc, bet) => acc + Number(bet?.sum || 0), 0)
  const myZoneSums = myBets.reduce(
    (acc, bet) => {
      const zoneKey = String(bet?.zone || '').toLowerCase()
      if (!WHEEL500_ZONE_KEYS.includes(zoneKey)) return acc
      acc[zoneKey] += Number(bet?.sum || 0)
      return acc
    },
    { x2: 0, x3: 0, x5: 0, x50: 0 }
  )

  const winZone = String(round?.win_zone || '').toLowerCase()
  const winSector = String(round?.win_sector ?? '—')
  const winMultiplier = Number(String(winZone || '').slice(1) || 0)
  const myWinningStake = Number(myZoneSums[winZone] || 0)
  const myPayout = myWinningStake * winMultiplier
  const myNetResult = myPayout - myTotalSum
  const myNetResultSign = myNetResult > 0 ? '+' : myNetResult < 0 ? '-' : ''

  const fullRoundId = String(round?.id || '')
  const shortRoundId = fullRoundId.length > SHORT_ROUND_ID_LENGTH
    ? `${fullRoundId.slice(0, SHORT_ROUND_ID_LENGTH)}…`
    : fullRoundId

  const zoneCfg = WHEEL500_ZONE_CONFIG[winZone]

  const handleCopyRoundId = async () => {
    if (!fullRoundId) return

    try {
      await navigator.clipboard.writeText(fullRoundId)
      hapticNotify('success')
      toast.success(t('wheel500.round.idCopied', 'ID раунда скопирован'))
    } catch (error) {
      hapticNotify('error')
      toast.error(t('wheel500.round.copyFailed', 'Не удалось скопировать ID'))
    }
  }

  if (isLoading) {
    return (
      <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
        <div className="mx-auto flex min-h-[60vh] max-w-7xl items-center justify-center">
          <Loader className="w-8 h-8 text-accent-primary animate-spin" />
        </div>
      </div>
    )
  }

  if (!round) {
    return (
      <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
        <div className="mx-auto max-w-7xl rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-red-300">
          {t('wheel500.round.empty', 'Раунд не найден')}
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8 overflow-x-hidden">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="relative flex items-center justify-center">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-0 inline-flex items-center justify-center rounded-xl border border-white/10 bg-surface p-2.5 text-white hover:text-accent-primary"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <h1 className="flex items-center gap-2 text-2xl lg:text-4xl font-bold text-accent-primary">
            <Target className="w-6 h-6 shrink-0 lg:w-8 lg:h-8" />
            <span>{t('wheel500.round.title', 'Раунд X50')}</span>
          </h1>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-6">
            <Wheel500Wheel
              sectors={WHEEL500_SECTORS}
              status="result"
              winSectorIndex={Number(round?.win_sector)}
              prevSectorIndex={Number.isInteger(round?.prev_round?.win_sector) ? round.prev_round.win_sector : undefined}
              roundId={round?.id}
              winZone={winZone}
            />

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {WHEEL500_ZONE_KEYS.map((zoneKey) => {
                const zoneItemCfg = WHEEL500_ZONE_CONFIG[zoneKey]
                const isWinner = winZone === zoneKey
                const value = Number(sums[zoneKey] || 0)

                return (
                  <div
                    key={zoneKey}
                    className="rounded-2xl border px-4 py-3"
                    style={{
                      borderColor: isWinner ? `${zoneItemCfg.hex}B3` : `${zoneItemCfg.hex}59`,
                      backgroundColor: isWinner ? `${zoneItemCfg.hex}33` : `${zoneItemCfg.hex}1a`
                    }}
                  >
                    <p className="text-sm font-medium" style={{ color: zoneItemCfg.hex }}>
                      {zoneItemCfg.label}
                    </p>
                    <p className="mt-1 inline-flex items-center gap-1 font-semibold text-white">
                      {value.toLocaleString()} <Gem className="w-3.5 h-3.5" />
                    </p>
                  </div>
                )
              })}
            </div>

            <Wheel500BetsFeed bets={bets} status="result" winColor={winZone} disableAnimation />
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-surface/50 p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="inline-flex items-center gap-2 min-w-0">
                  <p className="text-xs text-white/90 font-mono truncate">{shortRoundId}</p>
                  <button
                    type="button"
                    onClick={handleCopyRoundId}
                    className="inline-flex items-center justify-center rounded-md border border-white/15 bg-dark-primary/40 p-1.5 text-white/80 hover:text-white"
                    aria-label={t('wheel500.round.copyId', 'Скопировать ID раунда')}
                    title={t('wheel500.round.copyId', 'Скопировать ID раунда')}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-dark-primary/30 p-3 flex items-center justify-between gap-3">
                <p className="text-xs text-text-secondary">{t('wheel500.round.result', 'Результат')}</p>
                <div
                  className="w-16 h-16 rounded-full border-2 flex items-center justify-center text-base font-bold text-center px-1"
                  style={{
                    color: zoneCfg?.hex || '#fff',
                    backgroundColor: `${zoneCfg?.hex || '#fff'}2b`,
                    borderColor: `${zoneCfg?.hex || '#fff'}99`
                  }}
                >
                  {winSector}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-xl border border-white/10 bg-dark-primary/25 p-3">
                  <p className="text-xs text-text-secondary">{t('wheel500.history.totalBank', 'Банк')}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-white font-semibold">
                    {totalBank.toLocaleString()} <Gem className="w-3.5 h-3.5 text-accent-primary" />
                  </p>
                </div>

                <div className="rounded-xl border border-white/10 bg-dark-primary/25 p-3">
                  <p className="text-xs text-text-secondary">{t('wheel500.round.players', 'Участники')}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-white font-semibold">
                    {participantsCount} <Users className="w-3.5 h-3.5 text-text-secondary" />
                  </p>
                </div>
              </div>

              {myBets.length > 0 && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-white/10 bg-dark-primary/25 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-text-secondary">{t('wheel500.round.myBet', 'Ваша ставка')}</p>
                      <p className="inline-flex items-center gap-1 text-white font-semibold">
                        {myTotalSum.toLocaleString()} <Gem className="w-3.5 h-3.5 text-accent-primary" />
                      </p>
                    </div>

                    <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                      {WHEEL500_ZONE_KEYS.map((zoneKey) => {
                        const itemCfg = WHEEL500_ZONE_CONFIG[zoneKey]
                        return (
                          <div
                            key={`my-zone-${zoneKey}`}
                            className="rounded-lg border px-2 py-1"
                            style={{
                              color: itemCfg.hex,
                              borderColor: `${itemCfg.hex}59`,
                              backgroundColor: `${itemCfg.hex}1a`
                            }}
                          >
                            {myZoneSums[zoneKey].toLocaleString()}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-dark-primary/25 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-text-secondary">{t('wheel500.round.myResult', 'Ваш результат')}</p>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        myNetResult > 0
                          ? 'border border-emerald-300/35 bg-emerald-500/15 text-emerald-100'
                          : myNetResult < 0
                            ? 'border border-rose-300/35 bg-rose-500/15 text-rose-100'
                            : 'border border-white/20 bg-white/5 text-white'
                      }`}>
                        {myNetResult > 0
                          ? t('wheel500.history.youWon', 'Выигрыш')
                          : myNetResult < 0
                            ? t('wheel500.history.youLost', 'Проигрыш')
                            : t('wheel500.history.draw', 'Без изменений')}
                      </span>
                    </div>

                    <p className={`mt-1 inline-flex items-center gap-1 text-lg font-bold ${
                      myNetResult > 0 ? 'text-emerald-300' : myNetResult < 0 ? 'text-rose-300' : 'text-white'
                    }`}>
                      {myNetResultSign}{Math.abs(myNetResult).toLocaleString()} <Gem className="w-4 h-4" />
                    </p>

                    <div className="mt-1 text-xs text-text-secondary">
                      {t('wheel500.round.payout', 'Выплата')}: {myPayout.toLocaleString()} <Gem className="inline w-3 h-3 text-accent-primary" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Wheel500Round
